/**
 * 장지 시설 유형별 표기어.
 *
 * 6개 포털 모두 장지 시설 페이지의 title, description, keywords 에 "납골당"을
 * 하드코딩하고 있었다. 봉안당 483곳에는 맞는 말이지만 자연장 161곳과 묘지 592곳,
 * 즉 facilities_detail.json 1,236곳의 61%에는 틀린 표현이다.
 *
 * 네이버 SERP 조사(2026-08-10, 시설명 쿼리 60건)에서 노출된 title 51건 중 50건에
 * "납골당"이 두 번 들어가 있었다. 수목장 시설 제목에 "납골당"이 박히면
 * 관련성과 클릭률 양쪽에서 손해다.
 */
export type FacilityTypeKey = "NaturalBurial" | "Charnel" | "Cemetery";

interface FacilityTypeMeta {
  /** 시설명 뒤에 붙이는 정확한 유형 표기 */
  label: string;
  /**
   * 지역과 조합해 쓰는 검색어 표기.
   * label 과 다른 이유: "자연장"은 검색 의도가 캠핑장, 자연휴양림으로 해석된다.
   * 카테고리 쿼리 조사에서 "자연장" 15건 중 13건에 고캠핑, 8건에 숲나들e 가 노출됐고
   * "수목장"이 장례 의도로 잡히는 표현이었다.
   */
  searchLabel: string;
  /** 이 표현이 시설명에 이미 있으면 label 을 덧붙이지 않는다 */
  alias: RegExp;
}

const FACILITY_TYPE: Record<FacilityTypeKey, FacilityTypeMeta> = {
  NaturalBurial: { label: "자연장", searchLabel: "수목장", alias: /자연장|수목장|화초장|잔디장/ },
  Charnel: { label: "납골당", searchLabel: "납골당", alias: /납골|봉안/ },
  Cemetery: { label: "묘지", searchLabel: "묘지", alias: /묘지|묘원|공원묘/ },
};

/** 유형을 모를 때 쓰는 상위 표기. 현재 데이터는 3개 유형이 전부라 방어용이다. */
const FALLBACK_LABEL = "장지";

/**
 * @param type facilities_detail.json 의 type (NaturalBurial | Charnel | Cemetery)
 */
export const getFacilityTypeLabel = (type: string | undefined | null): string =>
  FACILITY_TYPE[type as FacilityTypeKey]?.label ?? FALLBACK_LABEL;

/**
 * 지역명과 붙여 쓰는 유형 표기 ("파주 수목장" 의 "수목장").
 *
 * @param type facilities_detail.json 의 type
 */
export const getFacilityRegionKeyword = (type: string | undefined | null): string =>
  FACILITY_TYPE[type as FacilityTypeKey]?.searchLabel ?? FALLBACK_LABEL;

/**
 * title, description 에 쓰는 시설 표시명.
 * 유형을 알 수 있는 표현이 시설명에 없을 때만 유형 표기를 덧붙인다.
 * (붙이면 "유토피아수목장 자연장" 처럼 겹친다)
 *
 * @param companyname facilities_detail.json 의 companyname
 * @param type facilities_detail.json 의 type
 */
export const getFacilityDisplayName = (
  companyname: string,
  type: string | undefined | null
): string => {
  const meta = FACILITY_TYPE[type as FacilityTypeKey];
  if (!meta) return companyname;
  return meta.alias.test(companyname) ? companyname : `${companyname} ${meta.label}`;
};
